/**
 * Which install path a device gets (PWA install prompt). Pure, so the rules
 * are tested without a browser; `currentDevice()` reads the real values.
 *
 * Mobile means a phone or tablet: a mobile user agent (or iPadOS, which
 * reports a Mac user agent with touch points), a coarse pointer, and a
 * screen whose short side is tablet-sized or smaller. A touch laptop or a
 * desktop browser in a narrow window is not mobile.
 */

import { isAndroidApp } from '../android/bridge.js';

export type InstallPlatform =
  /** Android (Chromium): the browser's own install prompt (beforeinstallprompt). */
  | 'android'
  /** iOS / iPadOS Safari: Share > Add to Home Screen, shown as steps. */
  | 'ios-safari'
  /** iOS Chrome, Firefox, Edge and in-app browsers: open in Safari first. */
  | 'ios-other'
  /** Desktop, or anything else: no automatic prompt. */
  | 'other';

export interface DeviceInfo {
  userAgent: string;
  /** navigator.maxTouchPoints */
  maxTouchPoints: number;
  /** matchMedia('(pointer: coarse)').matches */
  coarsePointer: boolean;
  /** screen.width / screen.height in CSS px */
  screenWidth: number;
  screenHeight: number;
}

/** Short side above this is not a phone or tablet (iPad Pro 12.9 is 1024). */
export const MAX_MOBILE_SHORT_SIDE = 1024;
/** Short side at or above this is a tablet rather than a phone. */
export const TABLET_SHORT_SIDE = 600;

const MOBILE_UA =
  /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle|KFAPWI|webOS|BlackBerry|BB10|Opera Mini|IEMobile/i;

/**
 * iOS browsers that are not Safari (they cannot add to the home screen the
 * Safari way, or are in-app web views without a Share > Add to Home Screen).
 */
const IOS_NOT_SAFARI =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo|Brave|GSA\/|FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|Twitter|LinkedInApp|Snapchat|MicroMessenger|Pinterest|TikTok|musical_ly|BytedanceWebview|WhatsApp/i;

export function isIos(d: DeviceInfo): boolean {
  if (/iPhone|iPad|iPod/.test(d.userAgent)) return true;
  // iPadOS 13+ asks for the desktop site by default: a Mac UA with touch.
  return /Macintosh/.test(d.userAgent) && d.maxTouchPoints > 1;
}

export function isMobileDevice(d: DeviceInfo): boolean {
  const uaMobile = MOBILE_UA.test(d.userAgent) || isIos(d);
  if (!uaMobile || !d.coarsePointer) return false;
  const shortSide = Math.min(d.screenWidth, d.screenHeight);
  return shortSide > 0 && shortSide <= MAX_MOBILE_SHORT_SIDE;
}

export function isTablet(d: DeviceInfo): boolean {
  return isMobileDevice(d) && Math.min(d.screenWidth, d.screenHeight) >= TABLET_SHORT_SIDE;
}

export function installPlatform(d: DeviceInfo): InstallPlatform {
  if (!isMobileDevice(d)) return 'other';
  if (isIos(d)) {
    // In-app web views usually drop the "Safari/" token as well.
    if (IOS_NOT_SAFARI.test(d.userAgent) || !/Safari\//.test(d.userAgent)) return 'ios-other';
    return 'ios-safari';
  }
  if (/Android/i.test(d.userAgent)) return 'android';
  return 'other';
}

/** The real device (browser only). */
export function currentDevice(): DeviceInfo {
  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    coarsePointer: coarse,
    screenWidth: window.screen?.width ?? window.innerWidth,
    screenHeight: window.screen?.height ?? window.innerHeight,
  };
}

export interface StandaloneProbe {
  matchMedia?: (query: string) => { matches: boolean };
  /** iOS Safari: true when opened from the home screen. */
  navigatorStandalone?: boolean;
  referrer?: string;
}

/** True when Velora is already running as the installed app. */
export function isStandalone(p: StandaloneProbe): boolean {
  if (p.navigatorStandalone === true) return true;
  const mm = p.matchMedia;
  if (mm) {
    for (const mode of ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay']) {
      if (mm(`(display-mode: ${mode})`).matches) return true;
    }
  }
  // An Android Trusted Web Activity opens with this referrer.
  return (p.referrer ?? '').startsWith('android-app://');
}

export function currentStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // Velora for Android is already an installed app: never offer the PWA.
  if (isAndroidApp()) return true;
  return isStandalone({
    ...(typeof window.matchMedia === 'function'
      ? { matchMedia: (q: string) => window.matchMedia(q) }
      : {}),
    navigatorStandalone: (navigator as { standalone?: boolean }).standalone === true,
    referrer: document.referrer,
  });
}
